import { initializeApp, getApp, getApps, type FirebaseApp } from "firebase/app";
import { initializeAppCheck, ReCaptchaV3Provider } from "firebase/app-check";
import {
  getAuth,
  setPersistence,
  indexedDBLocalPersistence,
  browserLocalPersistence,
  GoogleAuthProvider,
  FacebookAuthProvider,
  OAuthProvider,
} from "firebase/auth";
import { getFirestore } from "firebase/firestore";
import { getFunctions } from "firebase/functions";
import { getStorage } from "firebase/storage";


// You likely already have these values somewhere.
// Move them here (or import from an existing config file).
const firebaseConfig = {
     apiKey: "AIzaSyDajP2PUFvM87E4fj7FZ42_TWqVXgckhUU",
    authDomain: "appletoncc-6261b.firebaseapp.com",
    projectId: "appletoncc-6261b",
    storageBucket: "appletoncc-6261b.appspot.com",
    messagingSenderId: "652074739848",
    appId: "1:652074739848:web:2444ac74b8e6bef226973d",
};

export const app: FirebaseApp =
  getApps().length > 0 ? getApp() : initializeApp(firebaseConfig);

const appCheckSiteKey = String(import.meta.env.VITE_FIREBASE_APPCHECK_SITE_KEY ?? "").trim();

if (appCheckSiteKey) {
  if (import.meta.env.DEV && globalThis.location?.hostname === "localhost") {
    // Lets localhost use the App Check debug provider when enforcement is enabled.
    (globalThis as typeof globalThis & { FIREBASE_APPCHECK_DEBUG_TOKEN?: boolean }).FIREBASE_APPCHECK_DEBUG_TOKEN = true;
    console.info("Firebase App Check debug mode enabled for localhost.");
  }

  initializeAppCheck(app, {
    provider: new ReCaptchaV3Provider(appCheckSiteKey),
    isTokenAutoRefreshEnabled: true,
  });
} else if (import.meta.env.DEV && globalThis.location?.hostname === "localhost") {
  console.warn(
    "Firebase App Check is not configured. If Cloud Storage enforcement is enabled, localhost uploads will fail until VITE_FIREBASE_APPCHECK_SITE_KEY is set.",
  );
}

// ---------------------------------------------
// Auth
// ---------------------------------------------
export const auth = getAuth(app);

/**
 * IMPORTANT:
 * Explicit persistence for PWA reliability.
 *
 * indexedDBLocalPersistence is best for:
 * - PWAs
 * - iOS Safari
 * - multi-tab stability
 *
 * Fallback to browserLocalPersistence if needed.
 */
export const authPersistenceReady = setPersistence(auth, indexedDBLocalPersistence).catch(() => {
  // Fallback (older browsers / edge cases)
  return setPersistence(auth, browserLocalPersistence);
});

// ---------------------------------------------
// Firestore
// ---------------------------------------------
export const db = getFirestore(app);

// ---------------------------------------------
// Cloud Functions
// ---------------------------------------------
export const functions = getFunctions(app, "europe-west1");
export const storage = getStorage(app);

// ---------------------------------------------
// Auth Providers (centralised)
// ---------------------------------------------
export const googleProvider = new GoogleAuthProvider();
// Force account chooser every time (important on shared devices)
googleProvider.setCustomParameters({ prompt: "select_account" });

export const facebookProvider = new FacebookAuthProvider();

export const appleProvider = new OAuthProvider("apple.com");
appleProvider.addScope("email");
appleProvider.addScope("name");

// ---------------------------------------------
// Emulator wiring (optional, recommended for dev)
// ---------------------------------------------
// Uncomment when using Firebase emulators locally
/*
if (location.hostname === "localhost") {
  console.info("🔥 Using Firebase emulators");

  connectAuthEmulator(auth, "http://localhost:9099");
  connectFirestoreEmulator(db, "localhost", 8080);
  connectFunctionsEmulator(functions, "localhost", 5001);
}
*/
